import { MIN_CREDIT_PER_TX, PaymentMethodEnum } from "@garther/interfaces";
import { Box, Button, SelectableCard } from "@garther/ui/components";
import { onBtnClick, useI18n } from "@garther/ui/libs";
import { useCallback, useEffect, useState } from "react";

import { PaymentPopup } from "@/components/credit/payment-popup";
import { trackEngagement } from "@/libs/analytics";
import { useAuthStore } from "@/stores/auth.store";
import { useCreditStore } from "@/stores/credit.store";

interface PaymentMethodProps {
  value: PaymentMethodEnum;
  onChanged: (paymentMethod: PaymentMethodEnum) => void;
  totalAmount: number;
  error?: string;
}

export const PaymentMethod = ({ value, onChanged, totalAmount, error }: PaymentMethodProps) => {
  const { t, formatCredit } = useI18n();
  const { curUser } = useAuthStore();
  const { userCredits, loadUserCredits } = useCreditStore();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);

  const handleGetMoreCredits = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackEngagement("buy_credits_click", "payment");
    setShowPaymentPopup(true);
  }, []);

  useEffect(() => {
    if (curUser.profile?.id) {
      loadUserCredits(curUser.profile.id);
    }
  }, [curUser.profile?.id, loadUserCredits]);

  const hasEnoughCredits = userCredits && userCredits.balance >= totalAmount;
  const isCreditDisabled = !hasEnoughCredits && totalAmount > 0;

  const handleCreditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isCreditDisabled) {
      onChanged(PaymentMethodEnum.CREDIT);
    }
  };

  return (
    <Box className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-text-primary font-normal text-responsive-md">{t("booking.payment.title")}</p>
        <Button
          onClick={handleGetMoreCredits}
          variant="outline"
          padding="px-3 py-1"
          className="w-auto text-responsive-base"
        >
          {t("credits.getMoreCredits")}
        </Button>
      </div>

      <SelectableCard
        isSelected={value === PaymentMethodEnum.CREDIT}
        onClick={handleCreditClick}
        className={`flex items-center justify-between p-4 w-full ${
          isCreditDisabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="font-normal text-responsive-base">{t("booking.payment.credit")}</p>
            {userCredits && (
              <span className="text-sm text-text-secondary">
                {t("credits.balance")}: {formatCredit(userCredits.balance)}
              </span>
            )}
          </div>
          <p className="font-normal text-responsive-sm text-text-secondary">
            {t("booking.payment.creditDescription")}
          </p>
          {isCreditDisabled && totalAmount > 0 && (
            <p className="text-sm text-red-500 mt-1">
              {t("booking.payment.insufficientCredits", {
                required: formatCredit(totalAmount),
                available: formatCredit(userCredits?.balance || 0)
              })}
            </p>
          )}
        </div>
      </SelectableCard>

      <SelectableCard
        isSelected={value === PaymentMethodEnum.BANK_TRANSFER}
        onClick={onBtnClick(() => onChanged(PaymentMethodEnum.BANK_TRANSFER))}
        className="flex items-center justify-between p-4 w-full"
      >
        <div className="flex-1">
          <p className="font-normal text-responsive-base">{t("booking.payment.bankTransfer")}</p>
          <p className="font-normal text-responsive-sm text-text-secondary">
            {t("booking.payment.bankTransferDescription")}
          </p>
        </div>
      </SelectableCard>

      {/* <SelectableCard
        isSelected={value === PaymentMethodEnum.CARD}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onChanged(PaymentMethodEnum.CARD);
        }}
        className="flex items-center justify-between p-4 w-full"
      >
        <div className="flex-1">
          <p className="font-normal text-responsive-base">{t("booking.payment.creditCard")}</p>
          <p className="font-normal text-responsive-sm text-text-secondary">
            {t("booking.payment.creditCardDescription")}
          </p>
        </div>
      </SelectableCard> */}
      {error && <p className="text-sm text-red-400 mt-1">{error}</p>}
      <PaymentPopup
        isOpen={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
        initialCreditAmount={Math.max(MIN_CREDIT_PER_TX, totalAmount - (userCredits?.balance || 0))}
      />
    </Box>
  );
};
